import { useMemo } from "react";

/**
 * Custom hook to compute device statistics
 */
export function useDeviceStats(devices) {
  return useMemo(() => {
    if (!devices || devices.length === 0) {
      return { total: 0, online: 0, offline: 0, onlinePercent: 0 };
    }

    const total = devices.length;
    const online = devices.filter((device) => {
      // Check if device is online based on time_updated
      if (!device.time_updated) return false;
      const lastUpdated = new Date(device.time_updated);
      const now = new Date();
      const diffMinutes = (now - lastUpdated) / (1000 * 60);
      return diffMinutes <= 1440; // Consider online if updated within 24 hours (1440 minutes)
    }).length;
    const offline = total - online;
    const rawPercent = total > 0 ? (online / total) * 100 : 0;
    const onlinePercent =
      rawPercent % 1 === 0 ? rawPercent.toString() : rawPercent.toFixed(1);

    return { total, online, offline, onlinePercent };
  }, [devices]);
}

/**
 * Custom hook to compute recent alerts from Redux state
 */
export function useRecentAlerts(organisationAlerts, devices) {
  return useMemo(() => {
    if (!organisationAlerts || !Array.isArray(organisationAlerts)) return [];

    // Process organisation alerts - they should already be in the right format
    // If they have device_id, we can match with device names
    const processedAlerts = organisationAlerts
      .map((alert) => {
        // Find the device info if device_id exists
        const device = alert.device_id
          ? devices?.find((d) => d.id === alert.device_id)
          : null;

        return {
          ...alert,
          deviceName:
            device?.name || (alert.device_id ? "Unknown Device" : null),
          deviceId: alert.device_id,
          // Ensure we have required fields with fallbacks
          time_created: alert.time_created,
          message: alert.message || alert.action || "Alert triggered",
          severity: alert.severity || alert.level || "info",
        };
      })
      .filter((alert) => alert.time_created) // Only alerts with time_created
      .sort((a, b) => new Date(b.time_created) - new Date(a.time_created))
      .slice(0, 3);

    return processedAlerts;
  }, [organisationAlerts, devices]);
}

/**
 * Custom hook to check if user can add devices
 */
export function useCanAddDevice(
  selectedOrganisation,
  userDB,
  userOrganisationRoles,
  hasPermission
) {
  return useMemo(
    () =>
      selectedOrganisation?.owner_id === userDB?.id ||
      hasPermission(userOrganisationRoles, userDB?.id, "add"),
    [selectedOrganisation, userDB, userOrganisationRoles, hasPermission]
  );
}

/**
 * Custom hook to determine if data is loaded
 */
export function useIsDataLoaded(devices, organisationAlerts) {
  return useMemo(() => {
    return devices !== null && organisationAlerts !== null;
  }, [devices, organisationAlerts]);
}
